// Authors: everyone the app's library holds, grouped by era, filtered by tradition (FS-132 D5:
// breadth, never one author). A gallery of lettered plates in each author's tone (iOS FS-392), the
// name and dates under each; no portraits (unverified, FS-060), no counts of readers, no ranking.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { AuthorPlate } from "../../../components/AuthorPlate";
import { EmptyState } from "../../../components/Empty";
import { SectionHeader } from "../../../components/SectionHeader";
import { Skeleton } from "../../../components/Skeleton";
import ui from "../../../components/ui.module.css";
import { ERAS, eraOf, useAuthors, useCatalogue, type Author } from "../../../data/works";
import { morphFrom } from "../../../motion/shared";
import motionStyles from "../../../motion/motion.module.css";
import { useIndicator } from "../../../lib/useIndicator";
import { useTitle } from "../../../lib/useTitle";
import s from "./authors.module.css";

const ERA_KEYS = new Set<string>([...ERAS.map((e) => e.key), "Ante-Nicene", "Post-Nicene"]);

export const Route = createFileRoute("/_app/authors/")({
  validateSearch: (q: Record<string, unknown>): { tradition?: string } => (typeof q.tradition === "string" && q.tradition.length < 30 ? { tradition: q.tradition } : {}),
  component: AuthorsIndex,
});

function AuthorsIndex() {
  useTitle("Authors");
  const { tradition } = Route.useSearch();
  const { data: authors, isPending } = useAuthors();
  const { data: catalogue } = useCatalogue();
  const pills = useIndicator<HTMLDivElement>();

  const shown = useMemo(() => (authors ?? []).filter((a) => !a.collection && (!tradition || a.facets.includes(tradition))), [authors, tradition]);
  const traditions = useMemo(() => (catalogue?.authorFacets ?? []).filter((f) => !ERA_KEYS.has(f) && authors?.some((a) => a.facets.includes(f))), [catalogue, authors]);
  const readable = useMemo(() => new Set(catalogue?.works.filter((w) => w.text).map((w) => w.id)), [catalogue]);

  return (
    <div className={ui.pageWide}>
      <header className={s.head}>
        <h1 className={ui.title}>Authors</h1>
        <p className={ui.subtitle}>The writers in Bible Standard’s library, from the early church to the last century.</p>
      </header>

      <div className={s.filter}>
        <div className={`${ui.segmented} ${s.pills}`} ref={pills} role="radiogroup" aria-label="Tradition">
          {[undefined, ...traditions].map((t) => (
            <Link
              key={t ?? "all"}
              to="/authors"
              search={t ? { tradition: t } : {}}
              replace
              role="radio"
              aria-checked={tradition === t}
              className={tradition === t ? `${ui.seg} ${ui.segOn}` : ui.seg}
            >
              {t ?? "All"}
            </Link>
          ))}
        </div>
      </div>

      {isPending && (
        <div aria-busy="true" aria-label="Loading the authors" className={s.gallery}>
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} shape="block" height={180} />
          ))}
        </div>
      )}
      {ERAS.map((era) => {
        const people = shown.filter((a) => eraOf(a) === era.key);
        if (!people.length) return null;
        return (
          <section key={`${era.key}-${tradition ?? "all"}`} className={s.era} aria-labelledby={`era-${era.key}`}>
            <SectionHeader id={`era-${era.key}`} title={era.label} />
            <ul className={`${s.gallery} ${motionStyles.arrive}`}>
              {people.map((a) => (
                <AuthorCell key={a.id} a={a} readable={a.works.filter((w) => readable.has(w.libraryId)).length} />
              ))}
            </ul>
          </section>
        );
      })}
      {authors && shown.length === 0 && (
        <EmptyState compact title="No one in the library from that tradition yet." icon="people">
          <Link to="/authors" search={{}} className={ui.secondary}>
            Show everyone
          </Link>
        </EmptyState>
      )}
    </div>
  );
}

function AuthorCell({ a, readable }: { a: Author; readable: number }) {
  const works = a.works.length;
  return (
    <li>
      <Link to="/authors/$id" params={{ id: a.id }} className={s.cell} onClick={morphFrom("author", a.id)}>
        <span className={s.portrait} data-morph="author">
          <AuthorPlate name={a.name} dates={null} tone={a.tone} size={480} />
        </span>
        <span className={s.cellText}>
          <span className={s.name}>{a.name}</span>
          {a.dates && <span className={s.dates}>{a.dates.replace(/\s*-\s*/g, "–")}</span>}
          <span className={readable > 0 ? s.works : s.worksAway}>
            {works === 0 ? "Not yet in the library" : readable > 0 ? `${readable} to read here` : "In the Bible Standard app"}
          </span>
        </span>
      </Link>
    </li>
  );
}
